// web/src/components/ui/Field.jsx
// The shared auth text field. Login and Signup each had a private copy of this;
// ResetPassword had bare <input>s. One implementation now, so the show/hide
// password toggle exists in exactly one place.
//
// Styling is caller-controlled via inputClassName so this works both on the
// forest-green auth panels (the default) and on the light reset card, which
// uses the global .input class. Tailwind utilities sit in a later layer than
// .input, so the pl-10 / pr-12 appended below win over its px-3.

import { useState } from 'react';

const AUTH_INPUT =
  'w-full py-3 rounded-md bg-[#f5f1e8] text-ink-900 placeholder-ink-400 ' +
  'focus:outline-none focus:ring-2 focus:ring-emerald-500';

/**
 * @param icon             optional leading icon element
 * @param reveal           show a password show/hide toggle (password fields only)
 * @param inputClassName   overrides the default auth-panel input styling
 * @param wrapperClassName positioning context; must stay `relative`
 */
export default function Field({
  icon = null,
  value,
  onChange,
  reveal = false,
  type = 'text',
  inputClassName = AUTH_INPUT,
  wrapperClassName = 'relative',
  ...props
}) {
  // Local and never persisted, so navigating away or reloading is always masked.
  const [shown, setShown] = useState(false);

  return (
    <div className={wrapperClassName}>
      {icon && (
        // pointer-events-none so the icon never swallows a tap meant for the input
        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-400 pointer-events-none">
          {icon}
        </span>
      )}

      <input
        {...props}
        type={reveal && shown ? 'text' : type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={[inputClassName, icon ? 'pl-10' : '', reveal ? 'pr-12' : '']
          .filter(Boolean)
          .join(' ')}
      />

      {reveal && (
        <button
          type="button"
          onClick={() => setShown((s) => !s)}
          aria-label={shown ? 'Hide password' : 'Show password'}
          aria-pressed={shown}
          title={shown ? 'Hide password' : 'Show password'}
          // inset-y-0 makes the target the full height of the input, so it stays
          // a comfortable one-handed tap on a phone rather than an 18px icon.
          className="absolute right-0 inset-y-0 px-3.5 flex items-center rounded-r-md text-ink-400 hover:text-ink-700 focus:outline-none focus:text-ink-900 focus:ring-2 focus:ring-emerald-500"
        >
          {shown ? <EyeOffIcon /> : <EyeIcon />}
        </button>
      )}
    </div>
  );
}

// --- icons (inline SVG, currentColor; shared by Login and Signup) ---

function svgProps(size = 18) {
  return {
    width: size,
    height: size,
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 2,
    strokeLinecap: 'round',
    strokeLinejoin: 'round',
    'aria-hidden': true,
  };
}

export function UserIcon() {
  return (
    <svg {...svgProps()}>
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
      <circle cx="12" cy="7" r="4" />
    </svg>
  );
}

export function MailIcon() {
  return (
    <svg {...svgProps()}>
      <rect x="2" y="4" width="20" height="16" rx="2" />
      <path d="m22 7-10 6L2 7" />
    </svg>
  );
}

export function LockIcon() {
  return (
    <svg {...svgProps()}>
      <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
    </svg>
  );
}

function EyeIcon() {
  return (
    <svg {...svgProps()}>
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}

function EyeOffIcon() {
  return (
    <svg {...svgProps()}>
      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
      <line x1="1" y1="1" x2="23" y2="23" />
    </svg>
  );
}
